import React, { useState } from "react";
import { authToken, ROLES } from "utils/constant";
import {
  getIncomeStatement,
  getBalanceSheet,
  getCashFlowStatement,
} from "utils/api/private/reports.api";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Grid,
  TextField,
  Button,
  Divider,
  Alert,
  Stack,
} from "@mui/material";
import { useAuth } from "utils/context";
import {
  BalanceSheet,
  CashFlowStatement,
  IncomeStatement,
  ReportLineItem,
  AccountBalance,
} from "utils/types/Accounting.types";
import { ReportQuery } from "utils/types/reports.types";

// Helper for Income Statement & similar structures
const ReportLineItemSection = ({ title, data }: { title: string; data: { total: number; details: ReportLineItem[] } }) => (
  <Box sx={{ my: 2 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{title}</Typography>
    <TableContainer>
      <Table size="small">
        <TableBody>
          {data.details.map((item) => (
            <TableRow key={item.accountId} hover>
              <TableCell sx={{ pl: 2 }}>{item.accountName}</TableCell>
              <TableCell align="right">${item.total.toFixed(2)}</TableCell>
            </TableRow>
          ))}
          <TableRow sx={{ '& > *': { borderTop: '2px solid rgba(224, 224, 224, 1)', fontWeight: 'bold' } }}>
            <TableCell sx={{ pl: 2 }}>Total {title}</TableCell>
            <TableCell align="right">${data.total.toFixed(2)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

// Helper for Balance Sheet
const AccountBalanceSection = ({ title, data }: { title: string; data: { total: number; details: AccountBalance[] } }) => (
  <Box sx={{ my: 2 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{title}</Typography>
    <TableContainer>
      <Table size="small">
        <TableBody>
          {data.details.map((item) => (
            <TableRow key={item.accountId} hover>
              <TableCell sx={{ pl: 2 }}>{item.accountName}</TableCell>
              <TableCell align="right">${item.balance.toFixed(2)}</TableCell>
            </TableRow>
          ))}
          <TableRow sx={{ '& > *': { borderTop: '2px solid rgba(224, 224, 224, 1)', fontWeight: 'bold' } }}>
            <TableCell sx={{ pl: 2 }}>Total {title}</TableCell>
            <TableCell align="right">${data.total.toFixed(2)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

// Helper for Cash Flow Statement
const CashFlowSection = ({ title, data }: { title: string; data: { total: number; details: { description: string; amount: number }[] } }) => (
     <Box sx={{ my: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{title}</Typography>
        {/* Simplified for brevity, can be a table if needed */}
        {data.details.map((item, index) => (
            <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', pl: 2, py: 0.5 }}>
                <Typography variant="body2">{item.description}</Typography>
                <Typography variant="body2">${item.amount.toFixed(2)}</Typography>
            </Box>
        ))}
         <Box sx={{ display: 'flex', justifyContent: 'space-between', pl: 2, py: 0.5, borderTop: '2px solid rgba(224, 224, 224, 1)', fontWeight: 'bold' }}>
            <Typography variant="body1">Total from {title}</Typography>
            <Typography variant="body1">${data.total.toFixed(2)}</Typography>
        </Box>
    </Box>
);


export const FinancialStatementsDashboard = () => {
  const { role } = useAuth();
  const [filters, setFilters] = useState<ReportQuery>({
    startDate: new Date().toISOString().split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
  });

  const [incomeStatement, setIncomeStatement] = useState<IncomeStatement | null>(null);
  const [balanceSheet, setBalanceSheet] = useState<BalanceSheet | null>(null);
  const [cashFlow, setCashFlow] = useState<CashFlowStatement | null>(null);

  const [isLoading, setIsLoading] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value || undefined }));
  };

  const handleGenerateReport = async (reportType: "income" | "balance" | "cashflow") => {
    const token = localStorage.getItem(authToken);
    if (!token) return setError("Authentication failed.");

    setIsLoading(reportType);
    setError(null);
    
    let query: ReportQuery = { ...filters };
    if (reportType === 'balance') {
        // Balance sheet only needs an end date (asOfDate)
        query = { endDate: filters.endDate, country: filters.country };
    }

    try {
      if (reportType === "income") setIncomeStatement(await getIncomeStatement(token, query));
      else if (reportType === "balance") setBalanceSheet(await getBalanceSheet(token, query));
      else if (reportType === "cashflow") setCashFlow(await getCashFlowStatement(token, query));
    } catch (err: any) {
      setError(err.message || `Failed to generate ${reportType} report.`);
    } finally {
      setIsLoading("");
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      
       <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Filters</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <TextField fullWidth type="date" label="Start Date" name="startDate" value={filters.startDate} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} size="small" />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <TextField fullWidth type="date" label="End Date / As of Date" name="endDate" value={filters.endDate} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} size="small" />
          </Grid>
          {(role === ROLES.ADMIN || role === ROLES.MANAGER) && (
            <Grid item xs={12} sm={4} md={3}>
              <TextField fullWidth name="country" label="Country Code (e.g., CD)" value={filters.country || ''} onChange={handleFilterChange} size="small" />
            </Grid>
          )}
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Income Statement</Typography>
              <Button onClick={() => handleGenerateReport("income")} disabled={isLoading === 'income'}>
                {isLoading === 'income' ? <CircularProgress size={24} /> : "Generate"}
              </Button>
            </Stack>
            {isLoading === 'income' ? <CircularProgress/> : incomeStatement && (
              <>
                <ReportLineItemSection title="Revenues" data={incomeStatement.revenues} />
                <ReportLineItemSection title="Expenses" data={incomeStatement.expenses} />
                <Divider sx={{ my: 1 }} />
                <Typography variant="h6" sx={{ mt: 1, display: "flex", justifyContent: "space-between" }}>
                  <span>Net Income:</span>
                  <span style={{ color: incomeStatement.netIncome >= 0 ? "green" : "red" }}>${incomeStatement.netIncome.toFixed(2)}</span>
                </Typography>
              </>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Balance Sheet</Typography>
              <Button onClick={() => handleGenerateReport("balance")} disabled={isLoading === 'balance'}>
                {isLoading === 'balance' ? <CircularProgress size={24} /> : "Generate"}
              </Button>
            </Stack>
            {isLoading === 'balance' ? <CircularProgress/> : balanceSheet && (
              <>
                <AccountBalanceSection title="Assets" data={balanceSheet.assets} />
                <AccountBalanceSection title="Liabilities" data={balanceSheet.liabilities} />
                <AccountBalanceSection title="Equity" data={balanceSheet.equity} />
                <Divider sx={{ my: 1 }}/>
                 <Typography variant="h6" sx={{ mt: 2, color: balanceSheet.accountingEquationBalanced ? 'success.main' : 'error.main' }}>
                  Equation Balanced: {balanceSheet.accountingEquationBalanced ? "✔ Yes" : "✖ No"}
                </Typography>
              </>
            )}
          </Paper>
        </Grid>
        
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Statement of Cash Flows</Typography>
              <Button onClick={() => handleGenerateReport("cashflow")} disabled={isLoading === 'cashflow'}>
                {isLoading === 'cashflow' ? <CircularProgress size={24} /> : "Generate"}
              </Button>
            </Stack>
            {isLoading === 'cashflow' ? <CircularProgress/> : cashFlow && (
              <>
                <CashFlowSection title="Operating Activities" data={cashFlow.operatingActivities} />
                <CashFlowSection title="Investing Activities" data={cashFlow.investingActivities} />
                <CashFlowSection title="Financing Activities" data={cashFlow.financingActivities} />
                 <Divider sx={{ my: 1 }} />
                <Typography variant="h6" sx={{ mt: 1, display: "flex", justifyContent: "space-between" }}>
                  <span>Net Cash Flow:</span>
                  <span style={{ color: cashFlow.netCashFlow >= 0 ? 'green' : 'red' }}>${cashFlow.netCashFlow.toFixed(2)}</span>
                </Typography>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};